import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, User } from "lucide-react";
import { useLocation } from "wouter";
import { BlogPost } from "../shared/schema";

export default function BlogSection() {
  const [location] = useLocation();
  const isBlogPage = location === "/blog";
  const { data: posts, isLoading, error } = useQuery<BlogPost[]>(
    {
      queryKey: ['/api/blog'],
    });

  if (isLoading) {
    return (
      <section id="blog" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Blog de IA</h3>
            <p className="text-lg text-gray-600">Las últimas tendencias y noticias en inteligencia artificial</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="w-full h-48 bg-gray-300"></div>
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-6 bg-gray-300 rounded mb-2"></div>
                  <div className="h-16 bg-gray-300 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="blog" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Blog de IA</h3>
            <p className="text-lg text-red-600">Error al cargar los artículos del blog</p>
          </div>
        </div>
      </section>
    );
  }

  const samplePosts = [
    {
      id: 1,
      title: "El Futuro de los LLMs en 2024",
      excerpt: "Exploramos las tendencias más importantes en modelos de lenguaje grandes y su impacto en la industria.",
      author: "María González",
      category: "Tendencias",
      imageUrl: "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
      createdAt: new Date("2024-12-12"),
      blogUrl: "https://example.com/future-of-llms-2024",
    },
    {
      id: 2,
      title: "Cómo Implementar Transformer desde Cero",
      excerpt: "Una guía paso a paso para entender e implementar la arquitectura Transformer en Python.",
      author: "Carlos Ruiz",
      category: "Tutorial",
      imageUrl: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
      createdAt: new Date("2024-12-10"),
      blogUrl: "https://example.com/implementing-transformer",
    },
    {
      id: 3,
      title: "IA Generativa: Más Allá del Texto",
      excerpt: "Exploramos las aplicaciones de la IA generativa en música, video y otras modalidades.",
      author: "Ana López",
      category: "Investigación",
      imageUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
      createdAt: new Date("2024-12-08"),
      blogUrl: "https://example.com/generative-ai-beyond-text",
    },
  ];

  const displayPosts = posts && posts.length > 0 ? posts : samplePosts;

  return (
    <section id="blog" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Blog de IA</h3>
          <p className="text-lg text-gray-600">Las últimas tendencias y noticias en inteligencia artificial</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayPosts.slice(0, !isBlogPage ? 3 : displayPosts.length).map((post) => (
              <Card key={post.id} className="bg-white shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <img
                  src={post.imageUrl || "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400"}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    <Badge variant="secondary" className="mr-2">
                      {post.category}
                    </Badge>
                    <div className="flex items-center text-gray-500 text-sm ml-auto">
                      <Calendar className="w-4 h-4 mr-1" />
                      {post.createdAt && new Date(post.createdAt).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h4>
                  <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-300 rounded-full mr-2 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-600" />
                      </div>
                      <span className="text-sm text-gray-600">{post.author}</span>
                    </div>
                    <Button variant="ghost" className="text-emerald-600 font-medium hover:underline p-0 h-auto"
                      onClick={() => post.blogUrl && window.open(post.blogUrl, '_blank', 'noopener,noreferrer')}>
                      Leer más →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>

        {!isBlogPage && (
          <div className="text-center mt-12">
            <Button
              className="bg-emerald-600 text-white px-8 py-3 font-semibold hover:bg-emerald-700"
              onClick={() =>
                window.open(`/blog`, "_self", "noopener,noreferrer")
              }
            >
              Ver Todos los Artículos
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
